import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch } from "react-redux";
import {
  FaIdCard,
  FaPhoneAlt,
  FaTimes,
  FaEye,
} from "react-icons/fa";
import { setActivePage } from "../store/activePage";
import GetOperatorsList from "../hooks/getOperatorsList";
import formatDateTime from "../helpers/formatDateWithTime";

function OperatorAvatar({ name }) {
  const safeName = String(name || "Operator").trim() || "Operator";
  const initials = safeName
    .split(" ")
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-sm font-black text-blue-700 ring-1 ring-blue-200">
      {initials}
    </div>
  );
}

function maskAadhaar(value) {
  const digits = String(value || "").replace(/\s+/g, "");
  if (!digits) return "-";
  if (digits.length <= 4) return digits;
  return `**** **** ${digits.slice(-4)}`;
}

function buildEventLocation(event) {
  return [
    event.event_address,
    event.event_city,
    event.event_state,
    event.event_pincode,
  ]
    .filter(Boolean)
    .join(", ");
}

function SearchBar({ value, onChange }) {
  return (
    <div className="relative">
      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">
        S
      </span>
      <input
        type="text"
        placeholder="Search by operator, mobile, Aadhaar or event details..."
        value={value}
        onChange={onChange}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-4 text-sm text-slate-900 placeholder-slate-400 transition focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange({ target: { value: "" } })}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-lg leading-none text-slate-400 hover:text-slate-600"
        >
          x
        </button>
      )}
    </div>
  );
}

function EmptyState({ search }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center text-slate-500">
      <span className="mb-4 text-3xl font-black">{search ? "SR" : "OP"}</span>
      <p className="text-sm font-semibold text-slate-600">
        {search ? `No Operators found for "${search}"` : "No operators assigned yet"}
      </p>
      <p className="mt-1 text-xs text-slate-400">
        {search
          ? "Try another keyword or clear the search."
          : "Assigned operators will appear here once events have operators."}
      </p>
    </div>
  );
}

function EventListModal({ operator, onClose }) {
  if (!operator) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-white/80 bg-white shadow-2xl shadow-slate-300/70">
        <div className="flex items-start justify-between border-b border-slate-200 bg-[linear-gradient(135deg,#f8fbfd_0%,#eef7f8_48%,#e3f1ff_100%)] px-6 py-5">
          <div className="flex items-start gap-4">
            <OperatorAvatar name={operator.operator_name} />
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-600">
                Operators List
              </p>
              <h2 className="mt-1 text-2xl font-black text-slate-900">
                {operator.operator_name || "Operator Name"}
              </h2>
              <div className="mt-2 flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
                <span className="rounded-full bg-white px-3 py-1 ring-1 ring-slate-200">
                  Mobile: {operator.operator_mobile || "-"}
                </span>
                <span className="rounded-full bg-white px-3 py-1 ring-1 ring-slate-200">
                  Aadhaar: {maskAadhaar(operator.operator_aadhaar_number)}
                </span>
                <span className="rounded-full bg-blue-50 px-3 py-1 text-blue-700 ring-1 ring-blue-200">
                  {operator.total_events} event{operator.total_events === 1 ? "" : "s"}
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-800"
          >
            <FaTimes />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div>
              <p className="text-sm font-bold text-slate-900">
                Assigned Events
              </p>
              <p className="text-xs text-slate-500">
                Compact event list for faster scanning when this operator has many assignments.
              </p>
            </div>
            <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-slate-700 ring-1 ring-slate-200">
              {operator.total_events} total
            </span>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="max-h-[56vh] overflow-auto">
              <table className="min-w-full border-separate border-spacing-0">
                <thead className="sticky top-0 z-10 bg-slate-50">
                  <tr className="text-left">
                    <th className="border-b border-slate-200 px-4 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                      Event
                    </th>
                    <th className="border-b border-slate-200 px-4 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                      Date
                    </th>
                    <th className="border-b border-slate-200 px-4 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                      Customer
                    </th>
                    <th className="border-b border-slate-200 px-4 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                      Location
                    </th>
                    <th className="border-b border-slate-200 px-4 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {operator.events.map((event) => {
                    const location = buildEventLocation(event);
                    const isClosed = event.status === "Closed";

                    return (
                      <tr
                        key={`${operator.operator_id}-${event.event_id}`}
                        className="align-top transition hover:bg-slate-50/80"
                      >
                        <td className="border-b border-slate-100 px-4 py-3">
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                            Event #{event.event_id}
                          </p>
                          <p className="mt-1 text-sm font-bold text-slate-900">
                            {event.event_type || "Event"}
                          </p>
                        </td>
                        <td className="border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 whitespace-nowrap">
                          {event.event_date
                            ? formatDateTime(event.event_date)
                            : "-"}
                        </td>
                        <td className="border-b border-slate-100 px-4 py-3">
                          <p className="text-sm font-semibold text-slate-900">
                            {event.customer_name || "-"}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {event.customer_mobile || "No mobile"}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-400">
                            {[event.eventor_1, event.eventor_2].filter(Boolean).join(", ") || "No eventors"}
                          </p>
                        </td>
                        <td className="border-b border-slate-100 px-4 py-3 text-sm text-slate-700">
                          <p className="max-w-xs break-words">
                            {location || "-"}
                          </p>
                        </td>
                        <td className="border-b border-slate-100 px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${
                              isClosed
                                ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
                                : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                            }`}
                          >
                            {event.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const tableStyles = {
  table: {
    style: { backgroundColor: "transparent" },
  },
  headRow: {
    style: {
      backgroundColor: "#f4f8fb",
      borderBottom: "1px solid #dbe5ec",
    },
  },
  headCells: {
    style: {
      color: "#64748b",
      fontSize: "11px",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      backgroundColor: "transparent",
      borderBottom: "1px solid #e5edf3",
      color: "#1f2937",
      fontSize: "13px",
      "&:hover": {
        backgroundColor: "#f8fbfd",
      },
    },
  },
  cells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
      paddingTop: "12px",
      paddingBottom: "12px",
    },
  },
  pagination: {
    style: {
      backgroundColor: "#ffffff",
      borderTop: "1px solid #e5edf3",
      color: "#64748b",
      fontSize: "13px",
    },
    pageButtonsStyle: {
      color: "#64748b",
      fill: "#64748b",
      "&:hover:not(:disabled)": {
        backgroundColor: "#eef7f8",
      },
      "&:disabled": {
        opacity: 0.3,
      },
    },
  },
  noData: {
    style: {
      backgroundColor: "transparent",
      color: "#64748b",
    },
  },
};

function OperatorOperatorsList() {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOperator, setSelectedOperator] = useState(null);

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Operators List",
        activeMenu: "operatorsList",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      const response = await GetOperatorsList(dispatch);
      setOperators(response || []);
      setLoading(false);
    })();
  }, [dispatch]);

  const filteredData = useMemo(() => {
    const searchText = search.trim().toLowerCase();
    if (!searchText) return operators;

    return operators.filter((operator) => {
      const operatorMatch =
        String(operator.operator_name || "").toLowerCase().includes(searchText) ||
        String(operator.operator_mobile || "").toLowerCase().includes(searchText) ||
        String(operator.operator_aadhaar_number || "")
          .toLowerCase()
          .includes(searchText);

      const eventMatch = (operator.events || []).some((event) =>
        [
          event.event_id,
          event.event_type,
          event.customer_name,
          event.customer_mobile,
          event.eventor_1,
          event.eventor_2,
          event.event_city,
          event.event_state,
          event.status,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(searchText)),
      );

      return operatorMatch || eventMatch;
    });
  }, [operators, search]);

  const columns = [
    {
      name: "Operator",
      cell: (row) => (
        <div className="flex items-center gap-3 py-1">
          <OperatorAvatar name={row.operator_name} />
          <div>
            <p className="text-sm font-semibold text-slate-900">
              {row.operator_name || "-"}
            </p>
            <p className="text-xs text-slate-500">
              {row.total_events} assigned event{row.total_events === 1 ? "" : "s"}
            </p>
          </div>
        </div>
      ),
      sortable: true,
      sortFunction: (a, b) =>
        String(a.operator_name || "").localeCompare(String(b.operator_name || "")),
      grow: 2,
    },
    {
      name: "Mobile",
      selector: (row) => row.operator_mobile,
      cell: (row) => (
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <FaPhoneAlt className="text-xs text-blue-500" />
          <span className="font-mono">{row.operator_mobile || "-"}</span>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Aadhaar",
      selector: (row) => row.operator_aadhaar_number || "",
      cell: (row) => (
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <FaIdCard className="text-xs text-amber-500" />
          <span className="font-mono">{maskAadhaar(row.operator_aadhaar_number)}</span>
        </div>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Events",
      selector: (row) => row.total_events,
      cell: (row) => (
        <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700 ring-1 ring-blue-200">
          {row.total_events}
        </span>
      ),
      sortable: true,
      width: "110px",
    },
    {
      name: "View",
      cell: (row) => (
        <button
          type="button"
          title="View events"
          onClick={() => setSelectedOperator(row)}
          className="flex h-9 w-9 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 text-blue-700 transition hover:border-blue-300 hover:bg-blue-100 hover:text-blue-900"
        >
          <FaEye className="text-sm" />
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "90px",
    },
  ];

  return (
    <>
      <div className="min-h-screen bg-[linear-gradient(180deg,#edf5f8_0%,#f8fbfd_42%,#edf4f8_100%)] px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-6xl space-y-5">
          <div className="flex items-center justify-between rounded-3xl bg-[linear-gradient(135deg,#f8fbfd_0%,#edf7f6_50%,#d8f0ec_100%)] px-6 py-5 shadow-lg shadow-slate-200/70 ring-1 ring-white/70">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-blue-200 bg-blue-50 text-base font-black text-blue-700">
                OP
              </div>
              <div>
                <h1 className="text-lg font-black tracking-wide text-slate-900">
                  Operators List
                </h1>
                <p className="mt-0.5 text-xs text-slate-500">
                  {loading
                    ? "Loading operator assignments..."
                    : `${operators.length} operators assigned across your organisation events`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
              <span className="text-xs font-semibold text-emerald-600">
                {filteredData.length} shown
              </span>
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-white/80 bg-white/95 shadow-xl shadow-slate-200/70">
            <div className="border-b border-slate-200 px-4 py-3">
              <SearchBar
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            {loading ? (
              <div className="space-y-0">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="flex animate-pulse items-center gap-4 border-b border-slate-200 px-4 py-4"
                  >
                    <div className="h-10 w-10 rounded-xl bg-slate-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-40 rounded bg-slate-200" />
                      <div className="h-2.5 w-28 rounded bg-slate-200" />
                    </div>
                    <div className="h-3 w-24 rounded bg-slate-200" />
                    <div className="h-3 w-20 rounded bg-slate-200" />
                    <div className="h-9 w-9 rounded-xl bg-slate-200" />
                  </div>
                ))}
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                paginationPerPage={10}
                highlightOnHover
                customStyles={tableStyles}
                noDataComponent={<EmptyState search={search} />}
              />
            )}
          </div>
        </div>
      </div>

      <EventListModal
        operator={selectedOperator}
        onClose={() => setSelectedOperator(null)}
      />
    </>
  );
}

export default OperatorOperatorsList;
